import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Loader2, Building2, AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COUNTRIES = [
  { code: "GB", label: "United Kingdom" },
  { code: "IE", label: "Ireland" },
  { code: "DE", label: "Germany" },
  { code: "FR", label: "France" },
  { code: "ES", label: "Spain" },
  { code: "IT", label: "Italy" },
  { code: "NL", label: "Netherlands" },
  { code: "BE", label: "Belgium" },
  { code: "PT", label: "Portugal" },
  { code: "AT", label: "Austria" },
];

const GoCardlessConnectDialog = ({ open, onOpenChange }) => {
  const [country, setCountry] = useState("GB");
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [connectingId, setConnectingId] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    axios
      .get(`${API}/gocardless/institutions`, { params: { country }, withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        setInstitutions(res.data?.institutions || []);
      })
      .catch((e) => {
        if (cancelled) return;
        const detail = e?.response?.data?.detail;
        const msg =
          typeof detail === "string"
            ? detail
            : "Could not load bank list — please try again in a moment.";
        setError(msg);
        setInstitutions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, country]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return institutions;
    return institutions.filter((i) => i.name.toLowerCase().includes(q));
  }, [institutions, search]);

  const startConnect = async (inst) => {
    setConnectingId(inst.id);
    try {
      const res = await axios.post(
        `${API}/gocardless/connect`,
        { institution_id: inst.id },
        { withCredentials: true }
      );
      const link = res.data?.link;
      if (!link) {
        toast.error("GoCardless did not return a link");
        return;
      }
      // Full navigation to the bank consent screen
      window.location.assign(link);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start bank link");
      setConnectingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0E172C] border border-[#1A2849] text-white max-w-xl sm:rounded-3xl"
        data-testid="gocardless-connect-dialog"
      >
        <DialogHeader>
          <DialogTitle
            className="text-white text-base sm:text-xl tracking-tight"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Link a card via GoCardless
          </DialogTitle>
          <DialogDescription className="text-slate-300 text-xs sm:text-sm">
            Choose your bank. You'll be redirected to log in securely — CardWatch never sees
            your credentials. Only balance + account info is fetched.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {COUNTRIES.map((c) => (
            <button
              key={c.code}
              onClick={() => setCountry(c.code)}
              data-testid={`gc-country-${c.code}`}
              className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold border transition-colors ${
                country === c.code
                  ? "border-[#00E676] bg-[#00E676]/10 text-[#00E676]"
                  : "border-white/10 bg-white/[0.03] text-slate-300 hover:border-white/30"
              }`}
            >
              {c.code}
            </button>
          ))}
        </div>

        <div className="relative mt-3">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${COUNTRIES.find((c) => c.code === country)?.label || ""} banks`}
            data-testid="gc-search-input"
            className="w-full rounded-lg bg-[#060B19] border border-[#1A2849] pl-9 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#00E676] focus:ring-2 focus:ring-[#00E676]/20"
          />
        </div>

        {error && (
          <div
            className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300 flex items-start gap-2"
            data-testid="gc-error"
          >
            <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
            <span className="min-w-0 break-words">{error}</span>
          </div>
        )}

        <div
          className="mt-3 max-h-[400px] overflow-y-auto pr-1 space-y-1"
          data-testid="gc-institution-list"
        >
          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-400 text-sm gap-2">
              <Loader2 size={16} className="animate-spin" /> Loading banks…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm">
              {institutions.length === 0
                ? "No banks available for this country yet."
                : "No banks match that search."}
            </div>
          ) : (
            filtered.map((inst) => (
              <button
                key={inst.id}
                onClick={() => startConnect(inst)}
                disabled={connectingId !== null}
                data-testid={`gc-institution-${inst.id}`}
                className="w-full text-left flex items-center gap-3 rounded-lg border border-white/5 bg-white/[0.02] hover:bg-white/[0.06] hover:border-white/15 transition-colors px-3 py-2.5 disabled:opacity-50 disabled:cursor-wait"
              >
                <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {inst.logo ? (
                    <img
                      src={inst.logo}
                      alt=""
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <Building2 size={14} className="text-slate-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-medium truncate">{inst.name}</div>
                  {inst.bic && (
                    <div className="text-[10px] uppercase tracking-widest text-slate-400">
                      {inst.bic}
                    </div>
                  )}
                </div>
                {connectingId === inst.id && (
                  <Loader2 size={14} className="animate-spin text-[#00E676] flex-shrink-0" />
                )}
              </button>
            ))
          )}
        </div>

        <div className="mt-2 text-[10px] uppercase tracking-widest text-slate-500 text-center">
          Powered by GoCardless · PSD2 read-only access
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GoCardlessConnectDialog;
